import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import styles from './ScheduleTables.module.scss';
import ProfilePicture from '../../../../components/profilePicture/ProfilePicture';
import AllocationDetailsPopup from './AllocationDetailsPopup';
import AllocationFormModal from './AllocationFormModal';
import axiosClient from '../../../../api/axiosClient';
import { useToasts } from '../../../../contexts/ToastContext';
import { useAuth } from '../../../../contexts/AuthContext';
import SchedulePill from '../../../../components/schedulePill/SchedulePill';
import Modal from '../../../../components/modal/Modal';
import Button from '../../../../components/button/Button';
import Icon from '../../../../components/icon/Icon';
import Table from '../../../../components/table/Table';
import LoadingOverlay from '../../../../components/loadingOverlay/LoadingOverlay';

const StudentScheduleTable = ({
  data = [],
  weekDates = [],
  isLoading,
  allClinicalSites = [],
  allSpecialties = [],
  allInstructors = [],
  onRefresh,
  programId,
  waitingList = []
}) => {
  const { showToast } = useToasts();
  const { role, can } = useAuth();
  const canCreateSchedule = role !== 'Program Coordinator' || can('canCreateSchedule');

  // Details popup state
  const [selectedDateSchedules, setSelectedDateSchedules] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedDateLabel, setSelectedDateLabel] = useState(null);
  const [selectedRawDate, setSelectedRawDate] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);

  // Waiting list modal state
  const [waitlistModal, setWaitlistModal] = useState(null);
  const [processingWaitlistId, setProcessingWaitlistId] = useState(null);

  // Form modal state (edit / create)
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);

  const getScheduleForDate = (student, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return student.programSchedules?.filter(ps => ps.date === dateStr) || [];
  };

  const openDetailsPopup = (e, student, date) => {
    const cellRect = e.currentTarget.getBoundingClientRect();
    const POPUP_WIDTH = 340;
    const SPACING = 12;
    const CHECK_WIDTH = 440;
    let leftPosition;
    if (cellRect.right + SPACING + CHECK_WIDTH > window.innerWidth) {
      leftPosition = cellRect.left + window.scrollX - POPUP_WIDTH - SPACING;
    } else {
      leftPosition = cellRect.right + window.scrollX + SPACING;
    }

    setPopupPosition({ top: cellRect.top + window.scrollY, left: Math.max(0, leftPosition) });
    setSelectedDateSchedules(getScheduleForDate(student, date));
    setSelectedStudent(student);
    setSelectedDateLabel(format(date, 'EEEE, MM/dd/yyyy'));
    setSelectedRawDate(format(date, 'yyyy-MM-dd'));
  };

  const handleClosePopup = () => {
    setSelectedDateSchedules([]);
    setSelectedStudent(null);
    setSelectedDateLabel(null);
    setSelectedRawDate(null);
    setPopupPosition(null);
  };

  const openEditModal = (allocation) => {
    setEditingAllocation(allocation);
    setIsCreateMode(false);
    setIsFormModalOpen(true);
  };

  const openCreateModal = (student, date) => {
    setSelectedStudent(student);
    setSelectedRawDate(format(date, 'yyyy-MM-dd'));
    setEditingAllocation(null);
    setIsCreateMode(true);
    setIsFormModalOpen(true);
  };

  const handleDuplicate = (allocation) => {
    const student = selectedStudent;
    handleClosePopup();
    setSelectedStudent(student);
    setEditingAllocation(allocation);
    setIsCreateMode(true);
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingAllocation(null);
  };

  const handleUpdateAllocation = async (allocationId, updatedData) => {
    setIsUpdating(true);
    try {
      const response = await axiosClient.patch(`/program-schedules/${allocationId}`, updatedData);
      showToast(response.message, 'success');
      handleCloseFormModal();
      handleClosePopup();
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateAllocation = async (createData) => {
    setIsUpdating(true);
    try {
      const payload = {
        ...createData,
        studentId: selectedStudent.id,
      };
      const response = await axiosClient.post(`/program-schedules/${programId}/create-schedule`, payload);
      showToast(response.message, 'success');
      handleCloseFormModal();
      handleClosePopup();
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFormSubmit = (data) => {
    if (isCreateMode) {
      handleCreateAllocation(data);
    } else {
      handleUpdateAllocation(editingAllocation.id, data);
    }
  };

  const handleCancelAllocation = async (allocationId) => {
    setIsUpdating(true);
    try {
      const response = await axiosClient.patch(`/program-schedules/${allocationId}/cancel`);
      showToast(response.message, 'success');
      handleClosePopup();
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    setIsUpdating(true);
    try {
      const response = await axiosClient.delete(`/program-schedules/${allocationId}`);
      showToast(response.message, 'success');
      handleClosePopup();
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWaitlistAction = async (id, status) => {
    setProcessingWaitlistId(id);
    try {
      await axiosClient.patch(`/program-schedules/waitlist/${id}`, { status });
      setWaitlistModal(prev => ({
        ...prev,
        items: prev.items.filter(item => item.id !== id)
      }));
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setProcessingWaitlistId(null);
    }
  };

  if (isLoading && data.length === 0) {
    return <div className={styles.loading}>Loading schedules...</div>;
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={isLoading ? styles.tableLoading : ''}>
        <thead>
          <tr>
            <th>Student</th>
            <th>Specialty</th>
            <th>Clinical Site</th>
            {weekDates.map((date, idx) => {
              const today = new Date();
              const dateStr = format(date, 'yyyy-MM-dd');
              const isToday = dateStr === format(today, 'yyyy-MM-dd');
              const waitingCount = waitingList.filter(w => w.date === dateStr).length;
              return (
                <th key={idx}>
                  <div className={`${styles.dateHeader} ${isToday ? styles.activeDate : ''}`}>
                    <div className={styles.dayInfo}>
                      <span className={styles.dayNum}>{format(date, 'd')}</span>
                      <span className={styles.dayStr}>{format(date, 'EEE')}</span>
                    </div>
                    {waitingCount > 0 && (
                      <span
                        className={styles.waitingBadge}
                        title="Waiting List"
                        onClick={(e) => {
                          e.stopPropagation();
                          setWaitlistModal({
                            date: dateStr,
                            dateLabel: format(date, 'MMM d, yyyy'),
                            items: waitingList.filter(w => w.date === dateStr)
                          });
                        }}
                      >
                        {waitingCount}
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.id}>
              <td>
                <div className={styles.studentCell}>
                  <ProfilePicture
                    firstName={row.firstName}
                    lastName={row.lastName}
                    src={row.profilePicture}
                    size="xs"
                  />
                  <span title={`${row.firstName} ${row.lastName}`}>{`${row.firstName} ${row.lastName}`}</span>
                </div>
              </td>
              <td>
                <div className={styles.pillList}>
                  {row.specialities?.map(spec => (
                    <SchedulePill key={spec.id} text={spec.name} colorId={spec.id} variant="solid" />
                  ))}
                </div>
              </td>
              <td>
                <div className={styles.pillList}>
                  {row.clinicalSites?.map(site => (
                    <SchedulePill key={site.id} text={site.name} colorId={site.id} variant="outline" />
                  ))}
                </div>
              </td>
              {weekDates.map((date, dIdx) => {
                const daySchedules = getScheduleForDate(row, date);
                const isPast = date < new Date(new Date().toDateString());
                const firstSchedule = daySchedules[0];
                const moreCount = daySchedules.length - 1;
                return (
                  <td
                    key={dIdx}
                    className={`${daySchedules.length > 0 ? styles.allocatedCell : styles.emptyDay} allocated-cell`}
                    onClick={daySchedules.length === 0 && !isPast && canCreateSchedule ? () => openCreateModal(row, date) : undefined}
                  >
                    <div className={styles.cellContent}>
                      {firstSchedule && (
                        <div
                          className={styles.dateCellPills}
                          onClick={(e) => openDetailsPopup(e, row, date)}
                        >
                          <SchedulePill text={firstSchedule.speciality?.name} colorId={firstSchedule.speciality?.id} variant="solid" />
                          <SchedulePill text={firstSchedule.clinicalSite?.name} colorId={firstSchedule.clinicalSite?.id} variant="outline" />
                          {firstSchedule.instructor && (
                            <div className={styles.instructorRow}>
                              <ProfilePicture
                                firstName={firstSchedule.instructor.firstName}
                                lastName={firstSchedule.instructor.lastName}
                                src={firstSchedule.instructor.profilePicture}
                                size="xs"
                              />
                              <span className={styles.instructorName}>
                                {`${firstSchedule.instructor.firstName} ${firstSchedule.instructor.lastName}`}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {moreCount > 0 && (
                        <div
                          className={styles.moreRow}
                          onClick={(e) => openDetailsPopup(e, row, date)}
                        >
                          {moreCount} More
                        </div>
                      )}
                      {!isPast && canCreateSchedule && (
                        <div
                          className={styles.newAllocation}
                          onClick={() => openCreateModal(row, date)}
                        >
                          + New Allocation
                        </div>
                      )}
                      {daySchedules.length === 0 && isPast && '-'}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
          {data.length === 0 && !isLoading && (
            <tr>
              <td colSpan={weekDates.length + 3} className={styles.noData}>
                No student schedules found for this period.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {selectedDateLabel && (
        <AllocationDetailsPopup
          allocations={selectedDateSchedules}
          student={selectedStudent}
          date={selectedDateLabel}
          rawDate={selectedRawDate}
          position={popupPosition}
          onClose={handleClosePopup}
          onEdit={openEditModal}
          onDuplicate={handleDuplicate}
          onDelete={handleDeleteAllocation}
          onCancel={handleCancelAllocation}
          isLoading={isUpdating}
          suppressClickOutside={isFormModalOpen}
        />
      )}

      {isFormModalOpen && (
        <AllocationFormModal
          isCreateMode={isCreateMode}
          allocation={editingAllocation}
          initialDate={selectedRawDate}
          student={selectedStudent}
          allSpecialties={allSpecialties}
          allClinicalSites={allClinicalSites}
          allInstructors={allInstructors}
          isLoading={isUpdating}
          onClose={handleCloseFormModal}
          onSubmit={handleFormSubmit}
        />
      )}

      {waitlistModal && (
        <Modal scrollable={true}>
          <div className={styles.waitlistModal}>
            {processingWaitlistId && <LoadingOverlay />}
            <div className={styles.waitlistHeader}>
              <div>
                <h2 className={styles.waitlistTitle}>Waiting List</h2>
                <span className={styles.waitlistDate}>{waitlistModal.dateLabel}</span>
              </div>
              <button className={styles.waitlistCloseBtn} onClick={() => setWaitlistModal(null)}>
                <Icon name="plus" className={styles.crossIcon} />
              </button>
            </div>
            <Table
              columns={[
                {
                  header: 'Student',
                  render: (row) => (
                    <div className={styles.studentCell}>
                      <ProfilePicture
                        firstName={row.student?.firstName}
                        lastName={row.student?.lastName}
                        src={row.student?.profilePicture}
                        size="xs"
                      />
                      <span>{`${row.student?.firstName} ${row.student?.lastName}`}</span>
                    </div>
                  )
                },
                { header: 'Clinical Site', render: (row) => row.clinicalSite?.name || '-' },
                { header: 'Specialty', render: (row) => row.speciality?.name || '-' },
                {
                  header: 'Actions',
                  render: (row) => (
                    <div className={styles.waitlistActions}>
                      <Button
                        label="Add to Schedule"
                        variant="primary-light"
                        size="small"
                        isLoading={processingWaitlistId === row.id}
                        onClick={() => handleWaitlistAction(row.id, 'Approved')}
                      />
                      <Button
                        label="Cancel"
                        variant="secondary-light"
                        size="small"
                        isLoading={processingWaitlistId === row.id}
                        onClick={() => handleWaitlistAction(row.id, 'Cancelled')}
                      />
                    </div>
                  )
                },
              ]}
              data={waitlistModal.items}
              emptyMessage="No waiting list entries for this date."
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

StudentScheduleTable.propTypes = {
  data: PropTypes.array,
  weekDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  isLoading: PropTypes.bool,
  allClinicalSites: PropTypes.array,
  allSpecialties: PropTypes.array,
  allInstructors: PropTypes.array,
  onRefresh: PropTypes.func,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  waitingList: PropTypes.array
};

export default StudentScheduleTable;
